import { Calculator, Plus, X } from "lucide-react";
import { formatPrice } from "@/lib/store";
import { TradeWanted } from "./TradeWanted";
import { TradeInstallments } from "./TradeInstallments";

const money = (v) => (Number(v) > 0 ? formatPrice(v) : "R$ 0,00");
const sfxOf = (i) => (i === 0 ? "" : `-${i + 1}`);

const Breakdown = ({ est, sfx }) => (
  <ul className="mt-2 space-y-1 text-sm">
    <li className="flex justify-between text-zinc-300"><span>Valor do aparelho</span><span>{formatPrice(est.base)}</span></li>
    {est.deductions.map((d) => (
      <li key={d.key} data-testid={`trade-deduction-${d.key}${sfx}`} className="flex justify-between gap-3 text-amber-200"><span>{d.label}</span><span className="shrink-0">− {formatPrice(d.amount)}</span></li>
    ))}
    {est.bonus > 0 && (
      <li data-testid={`trade-bonus-line${sfx}`} className="flex justify-between gap-3 text-emerald-300"><span>Bônus: comprado na RC iPhones</span><span className="shrink-0">+ {formatPrice(est.bonus)}</span></li>
    )}
  </ul>
);

// Vários aparelhos: uma linha por aparelho e o total das avaliações.
const Several = ({ items, total }) => (
  <>
    <ul className="mt-3 space-y-3">
      {items.map(({ device, est, priced }, i) => (
        <li key={i} data-testid={`trade-estimate-item-${i + 1}`} className="rounded-2xl border border-white/10 p-3">
          <p className="flex justify-between gap-3 text-sm font-semibold text-white">
            <span className="min-w-0 truncate">{i + 1}. {device.model ? [device.model, device.storage].filter(Boolean).join(" ") : "Aparelho não escolhido"}</span>
            <span className="shrink-0">{est ? money(est.value) : device.model && !priced ? "A loja avalia" : "—"}</span>
          </p>
          {est && (est.deductions.length > 0 || est.bonus > 0) && <Breakdown est={est} sfx={sfxOf(i)} />}
        </li>
      ))}
    </ul>
    <p className="mt-4 text-xs uppercase tracking-[0.16em] text-zinc-400">Total das avaliações</p>
    <p data-testid="trade-estimate-value" className="font-display mt-1 text-4xl font-extrabold tracking-tight text-white">{money(total)}</p>
    {items.some((x) => !x.est) && <p className="mt-1 text-xs text-amber-200">Algum aparelho ainda sem valor: a loja completa a avaliação no WhatsApp.</p>}
  </>
);

// Avaliação estimada ao vivo e diferença para o(s) aparelho(s) que o cliente quer levar.
export const TradeEstimate = ({ items, total, allPriced, wants, setWant, addWant, removeWant, canAddWant, category, products, wantInfos, wantedTotal, diff, diffPay, setDiffPay }) => {
  const one = items.length === 1 ? items[0] : null;
  return (
    <div data-testid="trade-estimate" className="rounded-[24px] border border-white/15 bg-white/[0.05] p-5">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-300"><Calculator className="h-4 w-4" /> Avaliação estimada</p>
      {one && one.est && (
        <>
          <p data-testid="trade-estimate-value" className="font-display mt-2 text-4xl font-extrabold tracking-tight text-white">{money(one.est.value)}</p>
          <Breakdown est={one.est} sfx="" />
        </>
      )}
      {one && !one.est && (
        <p data-testid="trade-estimate-none" className="mt-2 text-sm text-zinc-200">
          {one.device.model && !one.priced ? "A loja vai avaliar e te responder no WhatsApp." : "Responda as perguntas para ver a avaliação."}
        </p>
      )}
      {!one && <Several items={items} total={total} />}
      <p className="mt-3 text-xs text-zinc-400">Sujeita à conferência do aparelho na loja.</p>
      {wants.map((w, i) => (
        <div key={i} className="relative">
          <TradeWanted f={{ ...w, category }} setF={setWant(i)} products={products} info={wantInfos[i]} sfx={sfxOf(i)}
            label={i === 0 ? "Qual aparelho você quer levar? (opcional)" : `Aparelho ${i + 1} para levar`} />
          {i > 0 && (
            <button type="button" data-testid={`trade-wanted-remove-${i + 1}`} onClick={() => removeWant(i)} aria-label="Remover aparelho"
              className="absolute right-0 top-0 rounded-full p-1 text-zinc-400 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
          )}
        </div>
      ))}
      {canAddWant && (
        <button type="button" data-testid="trade-wanted-add" onClick={addWant} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10">
          <Plus className="h-4 w-4" /> Levar mais um aparelho
        </button>
      )}
      {diff < 0 && (
        <div data-testid="trade-change" className="mt-4 rounded-2xl bg-[#25D366]/15 px-4 py-3 text-sm text-white">
          <p>Você recebe de volta: <strong data-testid="trade-change-value" className="text-[#25D366]">{formatPrice(-diff)}</strong> <span className="text-zinc-300">({money(total)} − {formatPrice(wantedTotal)}{wantInfos.some((x) => x?.order) ? ", preço médio sugerido" : ""})</span></p>
          <p className="mt-1 text-xs text-zinc-300">Seu aparelho vale mais do que o que você vai levar. A loja confirma o valor da volta no WhatsApp.</p>
        </div>
      )}
      {diff != null && diff >= 0 && (
        <p data-testid="trade-difference" className="mt-4 rounded-2xl bg-[#25D366]/15 px-4 py-3 text-sm text-white">
          Diferença estimada: <strong className="text-[#25D366]">{money(diff)}</strong> <span className="text-zinc-300">({formatPrice(wantedTotal)} − {money(total)}{wantInfos.some((x) => x?.order) ? ", preço médio sugerido" : ""})</span>
        </p>
      )}
      {diff == null && !allPriced && wantedTotal > 0 && items.length > 1 && <p className="mt-4 text-xs text-zinc-400">A diferença sai quando todos os aparelhos tiverem avaliação.</p>}
      {diff != null && <TradeInstallments amount={diff} pick={diffPay} onPick={setDiffPay} />}
    </div>
  );
};
